import { Platform } from 'react-native';
import { Stream } from '../stream';
import type InCallManagerType from 'react-native-incall-manager';
import { Analytics, ConferenceTypeEnum, SoundEnum } from '@matap/shared';
import { throttle } from '../../utils/throttler';
import { playLocalSound } from '../../utils/play.local.sound';

// Conditional imports to prevent web build from crashing on native-only modules
let mediaDevices: any;
let InCallManager: typeof InCallManagerType;

if (Platform.OS !== 'web') {
  const WebRTC = require('react-native-webrtc');
  mediaDevices = WebRTC.mediaDevices;
  InCallManager = require('react-native-incall-manager').default;
} else {
  mediaDevices = navigator.mediaDevices;
}

export class DeviceManager extends Stream<'change' | 'analytic'> {
  private readonly sessionId: string;

  public availableSpeakerDevices: MediaDeviceInfo[] = [];
  public availableMicDevices: MediaDeviceInfo[] = [];
  public availableCameraDevices: MediaDeviceInfo[] = [];

  private batteryRef?: any;
  private boundEmitBattery?: () => void;
  private selectedSpeakerDeviceId: string = '';
  private isSpeakerPhoneNative = false;
  private videoRefs = new Set<any>();
  private sound: { stop: () => void };
  private shouldRing = false;
  private tid: any;

  constructor(sessionId: string) {
    super();
    this.sessionId = sessionId;
    this.init();
  }

  private async init() {
    await this.updateDevices();
    mediaDevices.ondevicechange = () => throttle('device-change', 1000, { policy: 'replace' }, this.updateDevices);
    // this.startBatteryMonitoring();
  }

  private startBatteryMonitoring() {
    if (typeof navigator === 'undefined' || !('getBattery' in navigator)) return;
    (navigator as any).getBattery().then((battery: any) => {
      this.batteryRef = battery;

      this.boundEmitBattery = () => {
        this.emit('analytic', new Analytics.BatteryState(this.sessionId, { level: battery.level, charging: battery.charging }));
      };

      this.boundEmitBattery();
      this.batteryRef.addEventListener('levelchange', this.boundEmitBattery);
      this.batteryRef.addEventListener('chargingchange', this.boundEmitBattery);
      this.boundEmitBattery();
    });
  }

  public update = async () => {
    await this.updateDevices();
  };

  private updateDevices = async () => {
    if (this.isDestroyedOrDestroying()) return;

    try {
      const devices = await mediaDevices.enumerateDevices();
      this.availableSpeakerDevices = devices.filter((d: any) => d.kind === 'audiooutput' && d.deviceId !== '');
      this.availableMicDevices = devices.filter((d: any) => d.kind === 'audioinput' && d.deviceId !== '');
      this.availableCameraDevices = devices.filter((d: any) => d.kind === 'videoinput' && d.deviceId !== '');

      if (Platform.OS === 'web') {
        const stillAvailable = this.availableSpeakerDevices.find((d) => d.deviceId === this.selectedSpeakerDeviceId);
        if (!this.selectedSpeakerDeviceId || !stillAvailable) {
          this.selectedSpeakerDeviceId = this.availableSpeakerDevices[0]?.deviceId || 'default';
        }
      }

      this.emit('change');
      this.emit(
        'analytic',
        new Analytics.AudioRouteChanged(this.sessionId, {
          availableDevices: this.availableSpeakerDevices.map((d) => d.deviceId),
          selectedRoute: Platform.OS === 'web' ? this.selectedSpeakerDeviceId : this.isSpeakerPhoneNative ? 'speaker' : 'earpiece',
        }),
      );
      if (Platform.OS === 'web') this.handleWebSpeakerState();
    } catch (error) {
      console.error('[DeviceManager] Failed to enumerate devices:', error);
    }
  };

  public setSpeakerDevice(deviceIdOrType: string | boolean) {
    if (this.isDestroyedOrDestroying()) return;

    if (Platform.OS === 'web') {
      this.selectedSpeakerDeviceId = deviceIdOrType as string;
      this.handleWebSpeakerState();
    } else {
      const useSpeaker = deviceIdOrType === true || deviceIdOrType === 'speaker';
      this.isSpeakerPhoneNative = useSpeaker;

      if (InCallManager) {
        InCallManager.setForceSpeakerphoneOn(useSpeaker);
        InCallManager.setSpeakerphoneOn(useSpeaker);
      }
    }
    this.emit(
      'analytic',
      new Analytics.AudioRouteChanged(this.sessionId, {
        availableDevices: this.availableSpeakerDevices.map((d) => d.deviceId),
        selectedRoute: Platform.OS === 'web' ? this.selectedSpeakerDeviceId : this.isSpeakerPhoneNative ? 'speaker' : 'earpiece',
      }),
    );
    this.emit('change');
  }

  /**
   * Toggles the speakerphone strictly for Native platforms.
   * On Web, this safely does nothing (use setSpeakerDevice with a deviceId instead).
   */
  public toggleSpeaker() {
    if (this.isDestroyedOrDestroying() || Platform.OS === 'web') return;
    this.setSpeakerDevice(!this.isSpeakerPhoneNative);
  }

  /**
   * Returns the currently selected speaker.
   * Web: Returns the MediaDeviceInfo object.
   * Native: Returns 'speaker' or 'earpiece' so the UI knows the current route.
   */
  public getSelectedSpeakerDevice() {
    if (Platform.OS === 'web') {
      return this.availableSpeakerDevices.find((d) => d.deviceId === this.selectedSpeakerDeviceId)?.deviceId;
    }
    return this.isSpeakerPhoneNative ? 'speaker' : 'earpiece';
  }

  public onVideoElementLoaded(element: any) {
    if (Platform.OS !== 'web') return;
    this.videoRefs.add(element);
    this.handleWebSpeakerState();
  }

  public onVideoElementUnloaded(element: any) {
    if (Platform.OS !== 'web') return;
    this.videoRefs.delete(element);
  }

  private handleWebSpeakerState() {
    if (Platform.OS !== 'web' || !this.selectedSpeakerDeviceId || this.isDestroyedOrDestroying()) {
      return;
    }

    for (const element of this.videoRefs) {
      if ('setSinkId' in element) {
        element.setSinkId(this.selectedSpeakerDeviceId).catch((err: any) => {
          if (err.name !== 'SecurityError') console.warn(`[DeviceManager] setSinkId error: ${err.message}`);
        });
      }
    }
  }

  public startInCallService(type: ConferenceTypeEnum) {
    if (this.isDestroyedOrDestroying()) return;

    this.stopRing();

    if (Platform.OS !== 'web' && InCallManager) {
      InCallManager.start({ media: type === ConferenceTypeEnum.video ? 'video' : 'audio' });

      if (type === ConferenceTypeEnum.audio) {
        this.isSpeakerPhoneNative = false; // Sync internal state
        InCallManager.setSpeakerphoneOn(false);
        InCallManager.startProximitySensor();
      } else {
        this.isSpeakerPhoneNative = true; // Sync internal state
        InCallManager.setSpeakerphoneOn(true);
      }
    }
  }

  public stopInCallService() {
    this.stopRing();
    if (Platform.OS !== 'web' && InCallManager) {
      InCallManager.stopProximitySensor();
      InCallManager.stop();
    }
  }

  public startRing(initiator: boolean) {
    if (this.isDestroyedOrDestroying()) return;
    this.shouldRing = true;
    this.emit('analytic', new Analytics.RingStateChange(this.sessionId, { ringing: true, isInitiator: initiator }));
    /*  if (initiator) {*/
    const { setOnEndListener, stop } = playLocalSound(initiator ? SoundEnum.DIAL_TONE : SoundEnum.RINGING);
    this.sound = { stop };
    setOnEndListener(() => {
      this.sound = undefined;

      if (!this.shouldRing || this.isDestroyedOrDestroying()) {
        return;
      }

      if (this.shouldRing) {
        this.tid = setTimeout(() => {
          if (this.shouldRing) this.startRing(initiator);
        }, 1500);
      }
    });
    /*    } else if (Platform.OS !== 'web' && InCallManager) {
      InCallManager.startRingtone('_DEFAULT_', undefined, undefined, undefined);
    }*/
  }

  public stopRing() {
    if (!this.shouldRing) return;
    this.shouldRing = false;

    if (this.tid) clearTimeout(this.tid);

    if (this.sound) {
      this.sound.stop();
      this.sound = undefined;
    }

    if (Platform.OS !== 'web' && InCallManager) {
      InCallManager.stopRingtone();
    }

    this.emit('analytic', new Analytics.RingStateChange(this.sessionId, { ringing: false, isInitiator: false }));
  }

  protected onDestroy() {
    this.stopInCallService();

    if (mediaDevices) {
      mediaDevices.ondevicechange = null;
    }
    this.stopRing();

    this.availableSpeakerDevices = [];
    this.availableMicDevices = [];
    this.availableCameraDevices = [];
    this.videoRefs.clear();

    if (this.batteryRef && this.boundEmitBattery) {
      this.batteryRef.removeEventListener('levelchange', this.boundEmitBattery);
      this.batteryRef.removeEventListener('chargingchange', this.boundEmitBattery);
      this.batteryRef = undefined;
      this.boundEmitBattery = undefined;
    }
  }
}
